import React, { useState } from 'react';
import { MasterProtocol, ProtocolItem } from '../../types';

export const SUBCATEGORIES_MAP = {
    'Obra Gruesa': [
        'Enfierradura',
        'Moldaje',
        'Hormigón',
        'Excavación',
        'Estructura Metálica',
        'Anclajes',
        'Urbanización',
        'Relleno',
        'Trazado'
    ],
    'Terminaciones': [
        'Tabiquería',
        'Cielos',
        'Cubiertas',
        'Enlucidos',
        'Impermeabilización',
        'Muebles',
        'Pavimentos',
        'Revestimientos',
        'Cierres',
        'Ventanas',
        'Puertas'
    ],
    'Instalaciones': [
        'Clima',
        'Corrientes Débiles',
        'Detección de Incendios',
        'Extinción Contra Incendios',
        'Electricidad',
        'Sanitario',
        'Gases Clínicos'
    ]
};


interface ProtocolTemplateManagerProps {
    templates: MasterProtocol[];
    setTemplates: React.Dispatch<React.SetStateAction<MasterProtocol[]>>;
}

const ProtocolTemplateManager: React.FC<ProtocolTemplateManagerProps> = ({ templates, setTemplates }) => {
    // Modal states
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<MasterProtocol | null>(null);
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

    // Form states for creating/editing a protocol
    const [newProtocolName, setNewProtocolName] = useState('');
    const [newProtocolCode, setNewProtocolCode] = useState('');
    const [newProtocolCategory, setNewProtocolCategory] = useState<'Obra Gruesa' | 'Terminaciones' | 'Instalaciones' | ''>('');
    const [newProtocolSubcategory, setNewProtocolSubcategory] = useState('');
    const [newProtocolItems, setNewProtocolItems] = useState<ProtocolItem[]>([]);

    // Info modal state
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [activeItemId, setActiveItemId] = useState<string | null>(null);
    const [infoText, setInfoText] = useState('');
    const [infoReadOnly, setInfoReadOnly] = useState(false);

    // Sorting states
    const [sortField, setSortField] = useState<keyof MasterProtocol | 'itemCount' | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Filtering states
    const [globalSearch, setGlobalSearch] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterCode, setFilterCode] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterSubcategory, setFilterSubcategory] = useState('');

    const handleOpenCreate = () => {
        setEditingTemplateId(null);
        setNewProtocolName('');
        setNewProtocolCode('');
        setNewProtocolCategory('');
        setNewProtocolSubcategory('');
        setNewProtocolItems([]);
        setIsCreateOpen(true);
    };

    const handleOpenEdit = (template: MasterProtocol) => {
        setEditingTemplateId(template.id);
        setNewProtocolName(template.protocolName);
        setNewProtocolCode(template.code || '');
        setNewProtocolCategory(template.category as any || '');
        setNewProtocolSubcategory(template.subcategory || '');
        setNewProtocolItems(template.items || []);
        setIsCreateOpen(true);
    };

    const handleDeleteProtocol = (templateId: string, name: string) => {
        if (confirm(`¿Estás seguro de eliminar el protocolo maestro "${name}"?`)) {
            setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, isDeleted: true } : t));
        }
    };

    const handleAddParam = () => {
        const newItem: ProtocolItem = {
            id: Math.random().toString(36).substr(2, 9),
            text: '',
            additionalInfo: ''
        };
        setNewProtocolItems(prev => [...prev, newItem]);
    };

    const handleUpdateParamText = (id: string, text: string) => {
        setNewProtocolItems(prev => prev.map(item => item.id === id ? { ...item, text } : item));
    };

    const handleDeleteParam = (id: string) => {
        setNewProtocolItems(prev => prev.filter(item => item.id !== id));
    };

    const handleOpenInfo = (id: string, readOnly: boolean) => {
        setActiveItemId(id);
        setInfoReadOnly(readOnly);
        if (readOnly && selectedTemplate) {
            const item = selectedTemplate.items?.find(i => i.id === id);
            setInfoText(item?.additionalInfo || '');
        } else {
            const item = newProtocolItems.find(i => i.id === id);
            setInfoText(item?.additionalInfo || '');
        }
        setInfoModalOpen(true);
    };

    const handleSaveInfo = () => {
        if (activeItemId) {
            setNewProtocolItems(prev => prev.map(item => item.id === activeItemId ? { ...item, additionalInfo: infoText } : item));
        }
        setInfoModalOpen(false);
    };

    const handleSaveProtocol = () => {
        if (!newProtocolName.trim()) {
            alert('El Nombre del Protocolo es mandatorio.');
            return;
        }
        if (editingTemplateId) {
            setTemplates(prev => prev.map(t => t.id === editingTemplateId ? {
                ...t,
                protocolName: newProtocolName.trim(),
                code: newProtocolCode.trim() || undefined,
                category: newProtocolCategory || undefined,
                subcategory: newProtocolSubcategory.trim() || undefined,
                items: newProtocolItems
            } : t));
        } else {
            const newTemplate: MasterProtocol = {
                id: `mp-${Date.now()}`,
                protocolName: newProtocolName.trim(),
                code: newProtocolCode.trim() || undefined,
                category: newProtocolCategory || undefined,
                subcategory: newProtocolSubcategory.trim() || undefined,
                items: newProtocolItems
            };
            setTemplates(prev => [...prev, newTemplate]);
        }
        setIsCreateOpen(false);
        setEditingTemplateId(null);
    };

    const handleRowDoubleClick = (template: MasterProtocol) => {
        setSelectedTemplate(template);
        setIsViewOpen(true);
    };

    const handleSort = (field: keyof MasterProtocol | 'itemCount') => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Filter logic
    const filteredTemplates = templates.filter(t => {
        if (t.isDeleted) return false;
        // Global search
        const matchesGlobal = !globalSearch.trim() || 
            t.protocolName.toLowerCase().includes(globalSearch.toLowerCase()) ||
            (t.code || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
            (t.category || '').toLowerCase().includes(globalSearch.toLowerCase()) ||
            (t.subcategory || '').toLowerCase().includes(globalSearch.toLowerCase());

        // Header column filters
        const matchesName = !filterName.trim() || t.protocolName.toLowerCase().includes(filterName.toLowerCase());
        const matchesCode = !filterCode.trim() || (t.code || '').toLowerCase().includes(filterCode.toLowerCase());
        const matchesCategory = !filterCategory || t.category === filterCategory;
        const matchesSubcategory = !filterSubcategory.trim() || (t.subcategory || '').toLowerCase().includes(filterSubcategory.toLowerCase());

        return matchesGlobal && matchesName && matchesCode && matchesCategory && matchesSubcategory;
    });

    // Sorting logic
    const sortedTemplates = [...filteredTemplates].sort((a, b) => {
        if (!sortField) return 0;
        
        let valA: any = '';
        let valB: any = '';

        if (sortField === 'itemCount') {
            valA = a.items?.length || 0;
            valB = b.items?.length || 0;
        } else {
            valA = a[sortField] || '';
            valB = b[sortField] || '';
        }

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const renderSortIcon = (field: keyof MasterProtocol | 'itemCount') => {
        if (sortField !== field) return <span className="material-symbols-outlined text-[16px] opacity-30 group-hover:opacity-60 transition-opacity">swap_vert</span>;
        return sortDirection === 'asc' 
            ? <span className="material-symbols-outlined text-[16px] text-blue-550 font-bold">arrow_upward</span>
            : <span className="material-symbols-outlined text-[16px] text-blue-550 font-bold">arrow_downward</span>;
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200/60 dark:border-slate-800 shadow-sm">
                <div>
                    <h2 className="text-lg font-black text-slate-800 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-500 text-[24px]">fact_check</span>
                        Maestro de Protocolos
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">Administre las plantillas de protocolos de calidad estándar para la empresa.</p>
                </div>
                <button 
                    onClick={handleOpenCreate}
                    className="bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-bold shadow hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Agregar Protocolo
                </button>
            </div>

            {/* Global Search Bar */}
            <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-555 text-[20px]">search</span>
                <input 
                    type="text"
                    placeholder="Buscar por nombre, código, categoría o subcategoría..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white shadow-sm placeholder-slate-400"
                />
            </div>

            {/* Table layout with columns */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-x-auto">
                <table className="w-full text-left border-collapse select-none min-w-[950px]">
                    <thead>
                        {/* Title Headers with Sort */}
                        <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 text-xs uppercase tracking-wider">
                            <th 
                                onClick={() => handleSort('protocolName')}
                                className="p-3 font-bold cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                            >
                                <div className="flex items-center gap-1.5">
                                    Nombre del Protocolo
                                    {renderSortIcon('protocolName')}
                                </div>
                            </th>
                            <th 
                                onClick={() => handleSort('code')}
                                className="p-3 font-bold w-36 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                            >
                                <div className="flex items-center gap-1.5">
                                    Código
                                    {renderSortIcon('code')}
                                </div>
                            </th>
                            <th 
                                onClick={() => handleSort('category')}
                                className="p-3 font-bold w-48 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                            >
                                <div className="flex items-center gap-1.5">
                                    Categoría
                                    {renderSortIcon('category')}
                                </div>
                            </th>
                            <th 
                                onClick={() => handleSort('subcategory')}
                                className="p-3 font-bold w-48 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                            >
                                <div className="flex items-center gap-1.5">
                                    Subcategoría
                                    {renderSortIcon('subcategory')}
                                </div>
                            </th>
                            <th 
                                onClick={() => handleSort('itemCount')}
                                className="p-3 font-bold w-32 text-center cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors group"
                            >
                                <div className="flex items-center justify-center gap-1.5">
                                    Parámetros
                                    {renderSortIcon('itemCount')}
                                </div>
                            </th>
                            <th className="p-3 font-bold w-20 text-center">Editar</th>
                            <th className="p-3 font-bold w-20 text-center">Eliminar</th>
                        </tr>
                        {/* Filter inputs header row */}
                        <tr className="bg-slate-50/50 dark:bg-slate-900/30 border-b border-slate-200 dark:border-slate-800/60">
                            <td className="p-2">
                                <input 
                                    type="text"
                                    placeholder="Filtrar nombre..."
                                    value={filterName}
                                    onChange={(e) => setFilterName(e.target.value)}
                                    className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-white"
                                />
                            </td>
                            <td className="p-2">
                                <input 
                                    type="text"
                                    placeholder="Filtrar código..."
                                    value={filterCode}
                                    onChange={(e) => setFilterCode(e.target.value)}
                                    className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-white"
                                />
                            </td>
                            <td className="p-2">
                                <select
                                    value={filterCategory}
                                    onChange={(e) => {
                                        setFilterCategory(e.target.value);
                                        setFilterSubcategory('');
                                    }}
                                    className="w-full bg-white dark:bg-slate-955 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-white"
                                >
                                    <option value="">Todas</option>
                                    <option value="Obra Gruesa">Obra Gruesa</option>
                                    <option value="Terminaciones">Terminaciones</option>
                                    <option value="Instalaciones">Instalaciones</option>
                                </select>
                            </td>
                            <td className="p-2">
                                <select 
                                    disabled={!filterCategory}
                                    value={filterSubcategory}
                                    onChange={(e) => setFilterSubcategory(e.target.value)}
                                    className="w-full bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500 text-slate-800 dark:text-white disabled:bg-slate-100 dark:disabled:bg-slate-800/40 disabled:cursor-not-allowed"
                                >
                                    <option value="">Todas</option>
                                    {filterCategory && SUBCATEGORIES_MAP[filterCategory as keyof typeof SUBCATEGORIES_MAP]?.map(sub => (
                                        <option key={sub} value={sub}>{sub}</option>
                                    ))}
                                </select>
                            </td>
                            <td className="p-2 text-center text-[10px] font-bold text-slate-400">
                                {(filterName || filterCode || filterCategory || filterSubcategory || globalSearch) && (
                                    <button 
                                        onClick={() => {
                                            setFilterName('');
                                            setFilterCode('');
                                            setFilterCategory('');
                                            setFilterSubcategory('');
                                            setGlobalSearch('');
                                        }}
                                        className="text-red-550 hover:text-red-650 transition-colors hover:underline"
                                    >
                                        Limpiar
                                    </button>
                                )}
                            </td>
                            <td className="p-2"></td>
                            <td className="p-2"></td>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedTemplates.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="p-8 text-center text-slate-450 text-sm italic">No se encontraron protocolos maestros con los criterios de búsqueda.</td>
                            </tr>
                        ) : sortedTemplates.map(template => (
                            <tr 
                                key={template.id} 
                                onDoubleClick={() => handleRowDoubleClick(template)}
                                className="border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/10 transition-colors cursor-pointer"
                                title="Doble clic para ver detalles"
                            >
                                <td className="p-3.5">
                                    <span className="font-bold text-slate-800 dark:text-slate-200 text-sm">{template.protocolName}</span>
                                </td>
                                <td className="p-3.5 text-slate-500 dark:text-slate-400 text-sm font-medium">
                                    {template.code || '-'}
                                </td>
                                <td className="p-3.5 text-sm">
                                    {template.category ? (
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                                            template.category === 'Obra Gruesa' ? 'bg-orange-50 text-orange-700 dark:bg-orange-950/20 dark:text-orange-400' :
                                            template.category === 'Terminaciones' ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/20 dark:text-purple-400' :
                                            'bg-teal-50 text-teal-700 dark:bg-teal-950/20 dark:text-teal-400'
                                        }`}>
                                            {template.category}
                                        </span>
                                    ) : (
                                        <span className="text-slate-400 dark:text-slate-600 italic text-xs">Sin asignar</span>
                                    )}
                                </td>
                                <td className="p-3.5 text-slate-600 dark:text-slate-350 text-sm">
                                    {template.subcategory || '-'}
                                </td>
                                <td className="p-3.5 text-center text-slate-500 dark:text-slate-400 text-sm font-mono">
                                    {template.items?.length || 0}
                                </td>
                                <td className="p-3.5 text-center">
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEdit(template);
                                        }}
                                        className="text-indigo-500 hover:text-indigo-650 transition-colors p-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30"
                                        title="Editar Protocolo Maestro"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                    </button>
                                </td>
                                <td className="p-3.5 text-center">
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteProtocol(template.id, template.protocolName);
                                        }}
                                        className="text-red-500 hover:text-red-650 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                                        title="Eliminar Protocolo Maestro"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* View Modal */}
            {isViewOpen && selectedTemplate && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col h-[80vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">Ver Protocolo Maestro (Lectura)</h3>
                                <button onClick={() => setIsViewOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 dark:bg-slate-800/40 p-4 rounded-lg border border-slate-100 dark:border-slate-700">
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Nombre del Protocolo</label><p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{selectedTemplate.protocolName}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Código</label><p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{selectedTemplate.code || '-'}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Categoría</label><p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{selectedTemplate.category || '-'}</p></div>
                                <div><label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Subcategoría</label><p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{selectedTemplate.subcategory || '-'}</p></div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50">
                            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-12 text-center">#</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Parámetro de Revisión</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-32 text-center">Información</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {(selectedTemplate.items || []).map((item, index) => (
                                            <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                <td className="px-4 py-3 text-slate-500 font-mono text-sm w-12 text-center">{index + 1}</td>
                                                <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-sm">{item.text}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <button 
                                                        onClick={() => handleOpenInfo(item.id, true)} 
                                                        className={`text-blue-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 relative group ${item.additionalInfo ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200' : ''}`}
                                                        title="Ver información adicional"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">info</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {(selectedTemplate.items || []).length === 0 && (
                                            <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400 italic text-sm">No hay parámetros definidos.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-b-xl flex justify-end shrink-0">
                            <button onClick={() => setIsViewOpen(false)} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 shadow-sm transition-colors">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create / Edit Modal */}
            {isCreateOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl border border-slate-200 dark:border-slate-800 flex flex-col h-[80vh]">
                        <div className="p-6 border-b border-slate-200 dark:border-slate-700 shrink-0">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">
                                    {editingTemplateId ? 'Editar Protocolo Maestro' : 'Agregar Protocolo Maestro'}
                                </h3>
                                <button onClick={() => setIsCreateOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 dark:bg-slate-800/40 p-4 rounded-lg border border-slate-100 dark:border-slate-700">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Nombre del Protocolo <span className="text-red-500">*</span></label>
                                    <input 
                                        type="text" 
                                        placeholder="Ingrese el nombre del protocolo..." 
                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                        value={newProtocolName}
                                        onChange={(e) => setNewProtocolName(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Código Protocolo</label>
                                    <input 
                                        type="text" 
                                        placeholder="Ej: PRT" 
                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                        value={newProtocolCode}
                                        onChange={(e) => setNewProtocolCode(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Categoría</label>
                                    <select
                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white"
                                        value={newProtocolCategory}
                                        onChange={(e) => {
                                             const cat = e.target.value as any;
                                             setNewProtocolCategory(cat);
                                             setNewProtocolSubcategory(''); 
                                        }}
                                    >
                                        <option value="">Seleccione Categoría</option>
                                        <option value="Obra Gruesa">Obra Gruesa</option>
                                        <option value="Terminaciones">Terminaciones</option>
                                        <option value="Instalaciones">Instalaciones</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                        Subcategoría 
                                        {!newProtocolCategory && <span className="text-[9px] text-slate-400 dark:text-slate-550 lowercase font-normal ml-1">(seleccione categoría)</span>}
                                    </label>
                                    <select 
                                        disabled={!newProtocolCategory}
                                        className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white disabled:bg-slate-100 dark:disabled:bg-slate-800/40 disabled:cursor-not-allowed"
                                        value={newProtocolSubcategory}
                                        onChange={(e) => setNewProtocolSubcategory(e.target.value)}
                                    >
                                        <option value="">Seleccione Subcategoría</option>
                                        {newProtocolCategory && SUBCATEGORIES_MAP[newProtocolCategory as keyof typeof SUBCATEGORIES_MAP]?.map(sub => (
                                            <option key={sub} value={sub}>{sub}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50">
                            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-12 text-center">#</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Parámetro de Revisión</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-64 text-center">Configuración</th>
                                            <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {newProtocolItems.map((item, index) => (
                                            <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                                <td className="px-4 py-3 text-slate-500 font-mono text-sm w-12 text-center">{index + 1}</td>
                                                <td className="px-4 py-3">
                                                    <input 
                                                        type="text" 
                                                        placeholder="Ingrese el parámetro a revisar..." 
                                                        className="w-full border-none bg-transparent focus:ring-0 text-sm placeholder-slate-400 text-slate-850 dark:text-slate-200 outline-none" 
                                                        value={item.text} 
                                                        onChange={(e) => handleUpdateParamText(item.id, e.target.value)} 
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-3">
                                                        <button 
                                                            onClick={() => handleOpenInfo(item.id, false)} 
                                                            className={`text-blue-500 hover:text-blue-600 transition-colors p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 relative group ${item.additionalInfo ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200' : ''}`} 
                                                            title={item.additionalInfo || "Agregar información adicional"}
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">info</span>
                                                        </button>
                                                        <span className="text-green-600 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">check_circle</span></span>
                                                        <span className="text-red-500 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">cancel</span></span>
                                                        <span className="text-slate-400 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">remove_circle</span></span>
                                                        <span className="text-slate-400 opacity-40 cursor-not-allowed"><span className="material-symbols-outlined text-[20px]">chat</span></span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <button onClick={() => handleDeleteParam(item.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {newProtocolItems.length === 0 && (
                                            <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400 italic text-sm">No hay parámetros definidos. Pulse "+ Añadir Parámetros" para comenzar.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-b-xl flex justify-between items-center shrink-0">
                            <button 
                                onClick={handleAddParam} 
                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">add</span>
                                Añadir Parámetros
                            </button>
                            <div className="flex gap-3">
                                <button onClick={() => setIsCreateOpen(false)} className="px-4 py-2 text-slate-500 font-bold text-sm hover:text-slate-700 bg-slate-100 rounded-lg">Cancelar</button>
                                <button onClick={handleSaveProtocol} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 shadow-sm transition-colors">Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Modal */}
            {infoModalOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-lg border border-slate-200 dark:border-slate-800 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2"><span className="material-symbols-outlined text-blue-500">info</span>Información Adicional</h3>
                            <button onClick={() => setInfoModalOpen(false)} className="text-slate-400 hover:text-slate-600"><span className="material-symbols-outlined">close</span></button>
                        </div>
                        <div className="mb-4">
                            <textarea
                                className="w-full h-32 p-3 border border-slate-300 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                                placeholder={infoReadOnly ? "No hay información adicional." : "Escriba aquí los detalles..."}
                                value={infoText}
                                onChange={(e) => !infoReadOnly && setInfoText(e.target.value)}
                                readOnly={infoReadOnly}
                                autoFocus={!infoReadOnly}
                            />
                        </div>
                        <div className="flex justify-end gap-3 font-bold">
                            <button onClick={() => setInfoModalOpen(false)} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cerrar</button>
                            {!infoReadOnly && <button onClick={handleSaveInfo} className="px-6 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm">Guardar Información</button>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProtocolTemplateManager;
